import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PrivacySettings = ({ settings, onUpdate }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [localSettings, setLocalSettings] = useState(settings);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  
  // États pour les modales de confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  
  // Options de langue
  const languageOptions = [
    { value: 'fr', label: t('Français') },
    { value: 'en', label: t('English') }
  ];

  const visibilityOptions = [
    { value: 'public', label: t('Public - Visible par tous') },
    { value: 'verified', label: t('Utilisateurs Vérifiés Uniquement') },
    { value: 'private', label: t('Privé - Caché des autres') }
  ];

  const contactPreferenceOptions = [
    { value: 'whatsapp', label: t('WhatsApp uniquement') },
    { value: 'phone', label: t('Appels téléphoniques uniquement') },
    { value: 'both', label: t('WhatsApp & Téléphone') },
    { value: 'none', label: t('Pas de contact direct') }
  ];

  useEffect(() => {
    // Empêcher le scroll du body quand une modal est ouverte
    if (showDeleteModal || showLogoutModal || showResetModal || showSaveModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showDeleteModal, showLogoutModal, showResetModal, showSaveModal]);

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleLanguageChange = async (value) => {
    try {
      await i18n.changeLanguage(value);
      localStorage.setItem('Langue', value);
      setSelectedLanguage(value);
      handleSettingChange('language', value);
      
      alert(
        value === 'fr' 
          ? 'La langue a été changée en Français' 
          : 'Language has been changed to English'
      );
    } catch (error) {
      console.error('Error changing language:', error);
      alert('Impossible de changer la langue');
    }
  };

  const handleSave = () => {
    setShowSaveModal(true);
  };

  const confirmSave = () => {
    if (onUpdate) {
      onUpdate(localSettings);
    }
    setHasChanges(false);
    setShowSaveModal(false);
    alert(t('Paramètres sauvegardés avec succès'));
  };

  const handleReset = () => {
    setShowResetModal(true);
  };

  const confirmReset = () => {
    setLocalSettings(settings);
    setHasChanges(false);
    setShowResetModal(false);
    alert(t('Paramètres restaurés'));
  };

  const handleExportData = () => {
    alert(t('Fonctionnalité bientôt disponible'));
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteAccount = () => {
    // Logique de suppression de compte
    setShowDeleteModal(false);
    alert(t('Votre compte a été supprimé'));
    // Redirection vers la page d'accueil ou de connexion
    localStorage.clear();
    navigate('/');
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    localStorage.clear();
    setShowLogoutModal(false);
    navigate('/auth');
  };

  // Fonction de rendu des modales (même pattern que précédemment)
  const renderModal = (isOpen, onClose, title, message, confirmText, onConfirm, cancelText = 'Annuler', type = 'default') => {
    if (!isOpen) return null;

    const getIconColor = () => {
      switch(type) {
        case 'danger': return '#ef4444';
        case 'warning': return '#f59e0b';
        case 'success': return '#10b981';
        default: return '#3b82f6';
      }
    };

    const getIconName = () => {
      switch(type) {
        case 'danger': return 'AlertTriangle';
        case 'warning': return 'AlertCircle';
        case 'success': return 'CheckCircle';
        default: return 'Info';
      }
    };

    return (
      <div 
        className="fixed inset-0 z-50 bg-black bg-opacity-40 animate-fade-in flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-xl shadow-xl animate-slide-up-sm w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Contenu de la modal */}
          <div className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                type === 'danger' ? 'bg-red-50' : 
                type === 'warning' ? 'bg-yellow-50' : 
                type === 'success' ? 'bg-green-50' : 
                'bg-blue-50'
              }`}>
                <Icon 
                  name={getIconName()} 
                  size={24} 
                  className={type === 'danger' ? 'text-red-600' : 
                            type === 'warning' ? 'text-yellow-600' : 
                            type === 'success' ? 'text-green-600' : 
                            'text-blue-600'} 
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600">{message}</p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                className={`flex-1 px-4 py-3 font-semibold rounded-lg transition-colors ${
                  type === 'danger' 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : type === 'warning'
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : type === 'success'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title, icon, children) => (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon name={icon} size={20} className="text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">{t(title)}</h3>
      </div>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );

  const renderCheckbox = (label, description, settingKey) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-100">
      <div className="flex-1 mr-4">
        <label className="block text-sm font-medium text-gray-900 mb-1">{t(label)}</label>
        <p className="text-sm text-gray-600">{t(description)}</p>
      </div>
      <div className="relative inline-block w-12 h-6">
        <input
          type="checkbox"
          checked={localSettings[settingKey]}
          onChange={(e) => handleSettingChange(settingKey, e.target.checked)}
          className="sr-only"
          id={`checkbox-${settingKey}`}
        />
        <label
          htmlFor={`checkbox-${settingKey}`}
          className={`block w-12 h-6 rounded-full transition-colors duration-200 ease-in-out cursor-pointer ${
            localSettings[settingKey] ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
              localSettings[settingKey] ? 'transform translate-x-6' : ''
            }`}
          />
        </label>
      </div>
    </div>
  );

  const renderSelect = (label, description, settingKey, options, customOnChange = null) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-900 mb-2">{t(label)}</label>
      <p className="text-sm text-gray-600 mb-3">{t(description)}</p>
      <Select
        options={options}
        value={localSettings[settingKey]}
        onChange={customOnChange || ((value) => handleSettingChange(settingKey, value))}
        className="w-full"
      />
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* En-tête */}
      <div className="p-6 border-b border-gray-200">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('Confidentialité & Paramètres')}</h1>
          <p className="text-gray-600">
            {t('Contrôlez la visibilité de votre profil et vos préférences de communication')}
          </p>
        </div>
        
        {hasChanges && (
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={handleReset}
              className="flex-1 min-w-[120px]"
            >
              {t('Réinitialiser')}
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Save"
              iconPosition="left"
              className="flex-1 min-w-[120px] bg-blue-600 hover:bg-blue-700"
            >
              {t('Sauvegarder')}
            </Button>
          </div>
        )}
      </div>

      <div className="p-6">
        {/* Préférences de Langue */}
        {renderSection('Préférences de Langue', 'Globe',
          <>
            {renderSelect(
              'Langue de l\'interface',
              'Choisissez votre langue préférée pour l\'interface de l\'application',
              'language',
              languageOptions,
              handleLanguageChange
            )}
          </>
        )}

        {/* Paramètres de Notification */}
        {renderSection('Paramètres de Notification', 'Bell',
          <>
            {renderCheckbox(
              'Notifications par email',
              'Recevoir des mises à jour importantes par email',
              'emailNotifications'
            )}
            {renderCheckbox(
              'Notifications SMS',
              'Recevoir des notifications urgentes par SMS',
              'smsNotifications'
            )}
            {renderCheckbox(
              'Notifications WhatsApp',
              'Recevoir des notifications sur WhatsApp',
              'whatsappNotifications'
            )}
          </>
        )}

        {/* Données & Confidentialité */}
        {renderSection('Données & Confidentialité', 'Shield',
          <>
            {renderCheckbox(
              'Autoriser l\'analyse des données',
              'Aidez à améliorer la plateforme en partageant des données d\'utilisation anonymes',
              'allowAnalytics'
            )}
            {renderCheckbox(
              'Recommandations personnalisées',
              'Afficher des recommandations de pièces personnalisées basées sur votre activité',
              'personalizedRecommendations'
            )}
          </>
        )}

        {/* Sécurité du Compte */}
        {renderSection('Sécurité du Compte', 'Lock',
          <>
            {renderCheckbox(
              'Notifications de connexion',
              'Soyez informé lorsque quelqu\'un se connecte à votre compte',
              'loginNotifications'
            )}
          </>
        )}

        {/* Gestion des Données */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Gestion des Données')}</h3>
          
          <div className="space-y-4">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              <Icon name="LogOut" size={18} className="text-gray-600" />
              {t('Se déconnecter')}
            </button>
            
            <button
              onClick={handleDeleteAccount}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors font-medium"
            >
              <Icon name="Trash2" size={18} className="text-red-600" />
              {t('Supprimer le Compte')}
            </button>
          </div>
          
          <p className="text-xs text-gray-500 text-center mt-4 px-4">
            {t('La suppression du compte est permanente et ne peut pas être annulée. Toutes vos annonces et données seront supprimées.')}
          </p>
        </div>
      </div>

      {/* Modales de confirmation */}
      {renderModal(
        showDeleteModal,
        () => setShowDeleteModal(false),
        t('Supprimer le Compte'),
        t('Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.'),
        t('Supprimer'),
        confirmDeleteAccount,
        t('Annuler'),
        'danger'
      )}

      {renderModal(
        showLogoutModal,
        () => setShowLogoutModal(false),
        t('Se déconnecter'),
        t('Êtes-vous sûr de vouloir vous déconnecter ?'),
        t('Déconnexion'),
        confirmLogout,
        t('Annuler'),
        'warning'
      )}

      {renderModal(
        showResetModal,
        () => setShowResetModal(false),
        t('Réinitialiser les paramètres'),
        t('Êtes-vous sûr de vouloir réinitialiser tous les paramètres ?'),
        t('Réinitialiser'),
        confirmReset,
        t('Annuler'),
        'warning'
      )}

      {renderModal(
        showSaveModal,
        () => setShowSaveModal(false),
        t('Sauvegarder les modifications'),
        t('Êtes-vous sûr de vouloir sauvegarder les modifications apportées aux paramètres ?'),
        t('Sauvegarder'),
        confirmSave,
        t('Annuler'),
        'success'
      )}

      {/* Styles CSS */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp-sm {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slide-up-sm {
          animation: slideUp-sm 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PrivacySettings;